import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../lib/apiClient';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { GraduationCap, Plus, Trash2, Loader2, Edit2, X } from 'lucide-react';

const ExperienceManager = () => {
  const { toast } = useToast();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [formData, setFormData] = useState({
    role: '',
    company: '',
    location: '',
    duration: '',
    description: '',
    achievementsText: '',
    order_num: 0,
  });

  const parseAchievements = (value) =>
    value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

  const serializeAchievements = (value = []) => value.join('\n');

  const loadExperiences = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/admin/experience');
      setExperiences(data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load experience', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadExperiences();
  }, [loadExperiences]);

  const handleEdit = (exp) => {
    setEditingExp(exp);
    setFormData({
      role: exp.role || '',
      company: exp.company || '',
      location: exp.location || '',
      duration: exp.duration || '',
      description: exp.description || '',
      achievementsText: serializeAchievements(exp.achievements || []),
      order_num: exp.order_num ?? 0,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingExp(null);
    setFormData({
      role: '',
      company: '',
      location: '',
      duration: '',
      description: '',
      achievementsText: '',
      order_num: 0,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        role: formData.role,
        company: formData.company,
        location: formData.location,
        duration: formData.duration,
        description: formData.description,
        achievements: parseAchievements(formData.achievementsText),
        order_num: Number.parseInt(formData.order_num, 10) || 0,
      };
      if (editingExp) {
        await apiClient.put(`/api/admin/experience/${editingExp.id}`, payload);
        toast({ title: 'Success', description: 'Experience updated' });
      } else {
        await apiClient.post('/api/admin/experience', payload);
        toast({ title: 'Success', description: 'Experience created' });
      }
      await loadExperiences();
      handleCancel();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this experience?')) return;
    try {
      await apiClient.delete(`/api/admin/experience/${id}`);
      toast({ title: 'Success', description: 'Experience deleted' });
      await loadExperiences();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const newTemplate = {
    role: 'Role Title',
    company: 'Company Name',
    location: 'Location',
    duration: 'Month Year - Month Year',
    description: 'Description',
    achievementsText: '',
    order_num: 0,
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="experience-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GraduationCap className="w-8 h-8 text-cyan-500" />
          <h1 className="text-3xl font-bold text-white">Experience Management</h1>
        </div>
        {!isEditing && (
          <Button
            onClick={() => {
              setFormData(newTemplate);
              setIsEditing(true);
            }}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />Add Experience
          </Button>
        )}
      </div>

      {isEditing && (
        <div className="bg-neutral-900 border border-cyan-500/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">{editingExp ? 'Edit Experience' : 'Add New Experience'}</h2>
            <Button onClick={handleCancel} variant="ghost" size="sm"><X className="w-5 h-5" /></Button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Role</label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Company</label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Location</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Duration</label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Order</label>
                <Input
                  type="number"
                  value={formData.order_num}
                  onChange={(e) => setFormData({ ...formData, order_num: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Achievements (one per line)</label>
              <Textarea
                value={formData.achievementsText}
                onChange={(e) => setFormData({ ...formData, achievementsText: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={4}
                placeholder="Built X feature\nImproved Y performance"
              />
            </div>

            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-black">
              {editingExp ? 'Update' : 'Create'} Experience
            </Button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {experiences.map((exp) => (
          <div key={exp.id} className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-1">{exp.role}</h3>
                <p className="text-neutral-400 mb-2">{exp.company} | {exp.location}</p>
                <p className="text-sm text-neutral-500">{exp.duration}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleEdit(exp)} variant="outline" size="sm"><Edit2 className="w-4 h-4" /></Button>
                <Button onClick={() => handleDelete(exp.id)} variant="outline" size="sm" className="border-red-500/50 text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExperienceManager;
